import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export const TRACKER_STATUSES = ["Saved", "Applied", "Interview", "Offer", "Closed"];
const EMPTY = { items: [], counts: {}, total: 0 };
const SavedJobsContext = createContext({ ...EMPTY, loading: false, error: false, byJobId: {}, save: async () => {}, update: async () => {}, remove: async () => {}, refresh: () => {} });

const request = async (path, options = {}) => {
  const response = await fetch(`${API}/saved-jobs${path}`, { credentials: "include", headers: { "Content-Type": "application/json" }, ...options });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || "Request failed");
  }
  return response.json();
};

export function SavedJobsProvider({ children }) {
  const { user } = useAuth();
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) { setData(EMPTY); return; }
    setLoading(true);
    setError(false);
    try { setData(await request("")); } catch { setError(true); } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const save = useCallback(async (job) => {
    const saved = await request("", { method: "POST", body: JSON.stringify({ job_id: job.id }) });
    setData((previous) => previous.items.some((item) => item.id === saved.id) ? previous : withCounts([saved, ...previous.items]));
    return saved;
  }, []);

  const update = useCallback(async (savedId, patch) => {
    const fresh = await request(`/${savedId}`, { method: "PATCH", body: JSON.stringify(patch) });
    setData((previous) => withCounts(previous.items.map((item) => (item.id === savedId ? fresh : item))));
    return fresh;
  }, []);

  const remove = useCallback(async (savedId) => {
    await request(`/${savedId}`, { method: "DELETE" });
    setData((previous) => withCounts(previous.items.filter((item) => item.id !== savedId)));
  }, []);

  const value = useMemo(() => ({
    ...data, loading, error, refresh, save, update, remove,
    byJobId: Object.fromEntries(data.items.map((item) => [item.job_id, item])),
  }), [data, loading, error, refresh, save, update, remove]);
  return <SavedJobsContext.Provider value={value}>{children}</SavedJobsContext.Provider>;
}

const withCounts = (items) => ({ items, total: items.length, counts: Object.fromEntries(TRACKER_STATUSES.map((status) => [status, items.filter((item) => item.status === status).length])) });

export const useSavedJobs = () => useContext(SavedJobsContext);
